import { Component, EventEmitter, Input, Output } from '@angular/core';
import { ApiService } from 'src/app/shared/services/api.service';
import { FilterSettings } from './filter-settings';
import { getStatusColor, hotStatusColor, productionOverrideColor, shippedColor } from '../../master-schedule/shared';
import { MultiSelectOption } from 'src/app/shared/components/multi-select-dropdown/multi-select-dropdown';

@Component({
  selector: 'project-filters',
  templateUrl: './project-filters.html',
  styleUrl: './project-filters.scss',
  standalone: false,
})
export class ProjectFiltersComponent {

  constructor(private readonly api: ApiService) { }

  public filterMenuVisible: boolean = false;
  public filterMenuPosition = { x: 0, y: 0 };

  @Input() public filterSettings: FilterSettings = new FilterSettings();
  @Output() public filterSettingsChanged = new EventEmitter<any>();
  public tempFilterSettings: FilterSettings = new FilterSettings();
  public filterSettingsLastUpdated: Date = new Date(0);
  
  // Closure fixes to pass the correct context to multi-select dropdowns
  public getOptions = (type: string, search: string, maxResults?: number) => this.api.MasterSchedule.GetMasterScheduleFilterList(type, search, maxResults);
  public getStatusOptions = () => { return this.buildStatusOptions(); };

  openFilterMenu(event: MouseEvent) {
    this.tempFilterSettings = Object.assign(new FilterSettings(), this.filterSettings);
    this.filterMenuPosition.x = event.clientX + 10;
    this.filterMenuPosition.y = event.clientY + 10;
    this.filterMenuVisible = true;
  }

  closeFilterMenu() {
    this.filterMenuVisible = false;
  }

  commitTempFilterSettings() {
    this.filterSettings.product = this.tempFilterSettings.product;
    this.filterSettings.project = this.tempFilterSettings.project;
    this.filterSettings.endUser = this.tempFilterSettings.endUser;
    this.filterSettings.customer = this.tempFilterSettings.customer;
    this.filterSettings.poNumber = this.tempFilterSettings.poNumber;
    this.filterSettings.salesManager = this.tempFilterSettings.salesManager;
    this.filterSettings.technicalEngineer = this.tempFilterSettings.technicalEngineer;
    this.filterSettings.projectManager = this.tempFilterSettings.projectManager;
    this.filterSettings.planner = this.tempFilterSettings.planner;
    this.filterSettings.mechanicalEngineer = this.tempFilterSettings.mechanicalEngineer;
    this.filterSettings.mechanicalDrafter = this.tempFilterSettings.mechanicalDrafter;
    this.filterSettings.electricalEngineer = this.tempFilterSettings.electricalEngineer;
    this.filterSettings.electricalDrafter = this.tempFilterSettings.electricalDrafter;
    this.filterSettings.activity = this.tempFilterSettings.activity;
    this.filterSettings.status = this.tempFilterSettings.status;
    this.filterSettings.poStartDate = this.tempFilterSettings.poStartDate;
    this.filterSettings.poEndDate = this.tempFilterSettings.poEndDate;
    this.filterSettings.shipStartDate = this.tempFilterSettings.shipStartDate;
    this.filterSettings.shipEndDate = this.tempFilterSettings.shipEndDate;
    this.saveFilterSettings();
  }

  saveFilterSettings() {
    localStorage.setItem("masterScheduleFilterSettings", JSON.stringify(this.filterSettings));
  }

  onChange() {
    this.filterSettingsChanged.emit("");
    this.closeFilterMenu();
  }

  resetAllFilters() {
    this.filterSettings.searchText = '';
    this.filterSettings.product = '';
    this.filterSettings.project = '';
    this.filterSettings.endUser = '';
    this.filterSettings.customer = '';
    this.filterSettings.poNumber = '';
    this.filterSettings.salesManager = '';
    this.filterSettings.technicalEngineer = '';
    this.filterSettings.projectManager = '';
    this.filterSettings.planner = '';
    this.filterSettings.mechanicalEngineer = '';
    this.filterSettings.mechanicalDrafter = '';
    this.filterSettings.electricalEngineer = '';
    this.filterSettings.electricalDrafter = '';
    this.filterSettings.activity = '';
    this.filterSettings.status = '';
    this.filterSettings.poStartDate = null;
    this.filterSettings.poEndDate = null;
    this.filterSettings.shipStartDate = null;
    this.filterSettings.shipEndDate = null;
    this.saveFilterSettings();
    this.onChange();
  }

  toggleMultiSelect(type: string, event: MouseEvent) {
    //TODO: new dropdown control
  }
  hideMultiSelect() {
  }

  fromDateInputValue(value: string | null): Date | null {
    if (!value) {
      return null;
    }

    const parsed = this.parseDateOnly(value);
    return parsed ? this.normalizeDate(parsed) : null;
  }

  toDateInputValue(value: Date | string | null): string | null {
    if (!value) {
      return null;
    }

    const parsed = value instanceof Date ? value : this.parseDateOnly(value);
    if (!parsed || isNaN(parsed.getTime())) {
      return null;
    }

    const normalized = this.normalizeDate(parsed);

    const year = normalized.getFullYear();
    const month = String(normalized.getMonth() + 1).padStart(2, '0');
    const day = String(normalized.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private parseDateOnly(value: string): Date | null {
    const dateOnly = value.slice(0, 10);
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateOnly);
    if (!match) {
      const fallback = new Date(value);
      return isNaN(fallback.getTime()) ? null : fallback;
    }

    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    const localDate = new Date(year, month - 1, day);
    return isNaN(localDate.getTime()) ? null : localDate;
  }

  private normalizeDate(value: Date): Date {
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }

  private searchDelayed: any = undefined;
  searchTextChanged() {
    clearTimeout(this.searchDelayed);
    this.searchDelayed = setTimeout(() => {
      this.onChange();
    }, 500);
  }

  public formatDisplayValue(value: string | null | undefined): string {
    if (!value) {
      return '';
    }

    return value
      .split(';')
      .map(part => part.trim())
      .filter(part => part.length > 0)
      .join('; ');
  }

  public buildStatusOptions() {
    var filters: MultiSelectOption[] = [];
    const selection = this.filterSettings.status ? this.filterSettings.status.split(',') : [];
    const baseOption = { depth: 0, isParent: false, expanded: false, visible: true };
    filters.push({ ...baseOption, label: "On Time", selected: selection.includes("On Time"), blockColor: getStatusColor(2) });
    filters.push({ ...baseOption, label: "In Danger", selected: selection.includes("In Danger"), blockColor: getStatusColor(3) });
    filters.push({ ...baseOption, label: "On Hold", selected: selection.includes("On Hold"), blockColor: getStatusColor(5) });
    filters.push({ ...baseOption, label: "Late", selected: selection.includes("Late"), blockColor: getStatusColor(4) });
    filters.push({ ...baseOption, label: "Shipped", selected: selection.includes("Shipped"), blockColor: shippedColor });
    filters.push({ ...baseOption, label: "Production Override", selected: selection.includes("Production Override"), blockColor: productionOverrideColor });
    filters.push({ ...baseOption, label: "Hot", selected: selection.includes("Hot"), blockColor: hotStatusColor });
    return filters;
  }

}
